import { createFileRoute } from '@tanstack/react-router'
import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { FileDrop } from '@/components/file-drop'
import { Th, Td, fmt, fmtMes } from '@/components/data-table'
import { Trash2, AlertTriangle } from 'lucide-react'

export const Route = createFileRoute('/_protected/admin/dias-servicio')({
  component: DiasServicioPage,
})

type ResumenMes = {
  mes: string
  zonas: number
  dias: number
  posibles: number
  disponibilidad: number
  bajo80: { item: string; dias: number; posibles: number; pct: number }[]
}

type Informe = {
  fileName: string
  filas: number
  zonas: number
  meses: string[]
  resumen: ResumenMes[]
  conflictos: { item: string; mesPrevio: string; fechaPrevia: string; mesOtro: string; fechaOtra: string }[]
  avisos: { item: string | null; mes: string | null; texto: string }[]
  reemplazara: { mes: string; zonas: number }[]
}

type Cargado = {
  mes: string
  zonas: number
  dias: number
  disponibilidad: number
  bajo80: number
  archivo: string
}

function DiasServicioPage() {
  const [file, setFile] = React.useState<File | null>(null)
  const [token, setToken] = React.useState<string | null>(null)
  const [informe, setInforme] = React.useState<Informe | null>(null)
  const [fase, setFase] = React.useState<'inicio' | 'analizando' | 'revision' | 'cargando'>('inicio')
  const [error, setError] = React.useState<string | null>(null)
  const [exito, setExito] = React.useState<string | null>(null)
  const queryClient = useQueryClient()

  const { data: cargado, isLoading } = useQuery({
    queryKey: ['dias-servicio'],
    queryFn: async () => {
      const res = await fetch('/api/admin/dias-servicio', { credentials: 'include' })
      return res.json() as Promise<Cargado[]>
    },
  })

  function reset() {
    setFile(null); setToken(null); setInforme(null); setFase('inicio'); setError(null)
  }

  async function analizar(f: File) {
    setFile(f); setError(null); setExito(null); setInforme(null); setToken(null)
    setFase('analizando')
    try {
      const fd = new FormData()
      fd.append('file', f)
      fd.append('tipo', 'definitiva')
      const res = await fetch('/api/admin/preview', { method: 'POST', body: fd, credentials: 'include' })
      const data = await res.json()
      if (!res.ok) { setError(data.error ?? `Error ${res.status}`); setFase('inicio'); return }
      setToken(data.token); setInforme(data.informe); setFase('revision')
    } catch {
      setError('Error de red al analizar el archivo'); setFase('inicio')
    }
  }

  async function confirmar() {
    if (!token || !informe) return
    setFase('cargando'); setError(null)
    try {
      const res = await fetch('/api/admin/commit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, tipo: 'definitiva', fileName: informe.fileName }),
      })
      const data = await res.json()
      if (!res.ok) { setError(data.error ?? `Error ${res.status}`); setFase('revision'); return }
      setExito(`${fmt(data.insertadas)} filas cargadas (${data.meses.join(', ')}). Respaldo en ${data.respaldo}`)
      queryClient.invalidateQueries({ queryKey: ['dias-servicio'] })
      reset()
    } catch {
      setError('Error de red al cargar'); setFase('revision')
    }
  }

  async function borrarMes(mes: string) {
    if (!confirm(`¿Eliminar los días de servicio de ${fmtMes(mes)}?\nEse mes volverá a calcularse con la serie de ping.`)) return
    await fetch(`/api/admin/dias-servicio?mes=${encodeURIComponent(mes)}`, {
      method: 'DELETE', credentials: 'include',
    })
    queryClient.invalidateQueries({ queryKey: ['dias-servicio'] })
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Cargar días de servicio</CardTitle>
          <p className="text-sm text-muted-foreground">
            Archivo <span className="font-mono text-xs">Dashboard_BD ZONAS WIFI_DEFINITIVA.xlsx</span>, con una
            pestaña por mes. Es la fuente oficial de la disponibilidad del Dashboard. Sólo se
            reemplazan los meses que traiga el archivo.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <FileDrop
            file={file}
            disabled={fase === 'analizando' || fase === 'cargando'}
            onSelect={(f) => { if (f) analizar(f); else reset() }}
            onError={setError}
            hint="Arrastra el archivo DEFINITIVA aquí o haz clic para seleccionarlo"
          />

          {fase === 'analizando' && <p className="text-sm text-blue-700">Analizando el archivo…</p>}

          {informe && fase !== 'cargando' && (
            <div className="border rounded-lg divide-y">
              <div className="p-4 grid grid-cols-3 gap-3 text-sm">
                <Dato k="Meses" v={informe.meses.length ? informe.meses.map(fmtMes).join(', ') : '—'} />
                <Dato k="Zonas" v={String(informe.zonas)} />
                <Dato k="Filas" v={fmt(informe.filas)} />
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr><Th>Mes</Th><Th right>Zonas</Th><Th right>Disponibilidad</Th><Th>Bajo 80 %</Th></tr>
                  </thead>
                  <tbody>
                    {informe.resumen.map((r) => (
                      <tr key={r.mes}>
                        <Td>{fmtMes(r.mes)}</Td>
                        <Td right>{r.zonas}</Td>
                        <Td right>{r.disponibilidad.toFixed(2)} %</Td>
                        <Td>
                          {r.bajo80.length === 0
                            ? <span className="text-muted-foreground">ninguna</span>
                            : <span className="text-red-600">
                                {r.bajo80.map((z) => `${z.item} (${z.dias}/${z.posibles} = ${z.pct.toFixed(1)} %)`).join(', ')}
                              </span>}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {informe.conflictos.length > 0 && (
                <div className="p-4 text-sm bg-amber-50 space-y-1">
                  <p className="font-medium text-amber-900 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    {new Set(informe.conflictos.map((x) => x.item)).size} zonas con fecha de
                    instalación inconsistente entre hojas
                  </p>
                  <p className="text-amber-900/80">
                    Se usará la del mes de instalación (la más temprana). Conviene revisarlo en el archivo de origen.
                  </p>
                  <ul className="text-amber-900/80 list-disc pl-5 max-h-40 overflow-y-auto">
                    {informe.conflictos.map((x, i) => (
                      <li key={i} className="font-mono text-xs">
                        {x.item}: {x.mesPrevio}={x.fechaPrevia} vs {x.mesOtro}={x.fechaOtra}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {informe.avisos.length > 0 && (
                <div className="p-4 text-sm space-y-1">
                  <p className="font-medium">Avisos</p>
                  <ul className="list-disc pl-5 text-muted-foreground">
                    {informe.avisos.map((x, i) => (
                      <li key={i}>{[x.item, x.mes].filter(Boolean).join(' ')} {x.texto}</li>
                    ))}
                  </ul>
                </div>
              )}

              {informe.reemplazara.length > 0 && (
                <div className="p-4 text-sm bg-amber-50 text-amber-900">
                  Se reemplazarán los meses ya cargados:{' '}
                  <strong>{informe.reemplazara.map((r) => `${fmtMes(r.mes)} (${r.zonas} zonas)`).join(', ')}</strong>.
                  Los demás meses no se tocan.
                </div>
              )}

              <div className="p-4 flex gap-2 justify-end">
                <Button variant="outline" onClick={reset}>Cancelar</Button>
                <Button onClick={confirmar}>Confirmar y cargar</Button>
              </div>
            </div>
          )}

          {fase === 'cargando' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
              <span className="text-blue-700 font-medium">Cargando…</span>
            </div>
          )}
          {exito && <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm">{exito}</div>}
          {error && <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">{error}</div>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Meses cargados</CardTitle></CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Cargando…</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <Th>Mes</Th><Th right>Zonas</Th><Th right>Días</Th>
                    <Th right>Disponibilidad</Th><Th right>Bajo 80 %</Th><Th>Archivo</Th><Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {cargado?.map((m) => (
                    <tr key={m.mes} className="hover:bg-slate-50">
                      <Td>{fmtMes(m.mes)}</Td>
                      <Td right>{m.zonas}</Td>
                      <Td right>{fmt(m.dias)}</Td>
                      <Td right>{m.disponibilidad.toFixed(2)} %</Td>
                      <Td right>
                        {m.bajo80 > 0
                          ? <span className="text-red-600 font-semibold">{m.bajo80}</span>
                          : <span className="text-muted-foreground">0</span>}
                      </Td>
                      <Td><span className="text-xs text-muted-foreground">{m.archivo}</span></Td>
                      <td className="px-2 py-2 text-right border-b">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => borrarMes(m.mes)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {cargado?.length === 0 && (
                    <tr><td colSpan={7} className="p-6 text-sm text-center text-muted-foreground">
                      No hay días de servicio cargados. El Dashboard usará la serie de ping.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function Dato({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{k}</p>
      <p className="font-semibold">{v}</p>
    </div>
  )
}
